import React, { useState, useEffect, useCallback } from "react";
import { api } from "../pages/api";
import "bootstrap/dist/css/bootstrap.min.css";
import {
    Button,
    Modal,
    Row,
    Col,
    Form,
    Table,
    Pagination, Alert, Spinner,
} from "react-bootstrap";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const BillingData = () => {
    const [billingData, setBillingData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 15;
    const [totalPages, setTotalPages] = useState(1);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({
        date: new Date().toISOString().slice(0, 10),
        revenue_type: "scheme",
        revenue_amount: "",
        revenue_desc: "",
    });
    const [showEditModal, setShowEditModal] = useState(false);
    const [editFormData, setEditFormData] = useState({});
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");
    const [saving, setSaving] = useState(false);

    const formatDate = (isoDate) => {
        if (!isoDate || isNaN(new Date(isoDate).getTime())) {
            return "N/A"; // Handle null, empty, or invalid dates
        }
        const date = new Date(isoDate);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-based
        const year = date.getFullYear();

        return `${day}-${month}-${year}`;
    };

    const fetchBillingData = useCallback(async () => {
        setLoading(true);
        setErrorMessage("");
        try {
            const { data } = await api.get(
                `/api/addon/getbyidall?page=${currentPage}&limit=${itemsPerPage}`
            );
            setBillingData(data.data);
            setTotalPages(data.totalPages);
        } catch (error) {
            setErrorMessage(error.response?.data?.message || "Unable to load revenue records.");
        } finally {
            setLoading(false);
        }
    }, [currentPage]);

    useEffect(() => {
        fetchBillingData();
    }, [fetchBillingData]);

    const handleEditChange = (e) => {
        setEditFormData({ ...editFormData, [e.target.name]: e.target.value });
    };

    const handleSaveChanges = async () => {
        if (!editFormData.date || !editFormData.revenue_type || Number(editFormData.revenue_amount) <= 0) return toast.warning("Enter a valid date, type, and positive amount.");
        if ((editFormData.revenue_desc || "").trim().length > 500) return toast.warning("Description cannot exceed 500 characters.");
        if (!window.confirm("Save changes to this revenue record?")) return;
        setSaving(true);
        try {
            await api.put(`/api/addon/update/${editFormData.id}`, editFormData);
            toast.success("Revenue record updated successfully.");
            await fetchBillingData();
            setShowEditModal(false);
        } catch (error) {
            toast.error(error.response?.data?.message || "Unable to update revenue record.");
        } finally {
            setSaving(false);
        }
    };

    const handleShow = () => setShowModal(true);
    const handleClose = () => setShowModal(false);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault(); // Prevent the default form submission behavior
        if (!formData.date || !formData.revenue_type || Number(formData.revenue_amount) <= 0) return toast.warning("Enter a valid date, type, and positive amount.");
        if (formData.revenue_desc.trim().length > 500) return toast.warning("Description cannot exceed 500 characters.");
        if (!window.confirm("Add this revenue record?")) return;

        setSaving(true);
        try {
            // Post form data to the API
            await api.post(
                "/api/addon/create",
                formData
            );

            // Notify the user of success
            toast.success("Revenue record added successfully.");

            // Fetch updated billing data to refresh the list or UI
            await fetchBillingData();

            // Reset the form fields after submission
            setFormData({
                date: new Date().toISOString().slice(0, 10),
                revenue_type: "scheme",
                revenue_amount: "",
                revenue_desc: ""
            });


            // Close the modal popup
            handleClose();
        } catch (error) {
            console.error("Error adding billing data:", error);

            // Handle errors based on API response or network issues
            if (error.response) {
                // Display the error message returned by the API
                toast.error(error.response.data.message || "Unable to add revenue record.");
            } else {
                // Handle generic network errors
                toast.error("Network error. Please try again.");
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="container mt-2">
            <div className="d-flex justify-content-between mb-2">
                <h3>Revenue Data</h3>
                <Button variant="success" onClick={handleShow}>
                    Add New Entry
                </Button>
            </div>
            {errorMessage && <Alert variant="danger">{errorMessage} <Button size="sm" variant="outline-danger" onClick={fetchBillingData}>Retry</Button></Alert>}
            {loading ? <div className="text-center p-5"><Spinner /><p className="text-muted mt-2">Loading revenue records...</p></div> :
            <Table striped hover responsive className="table-bordered">
                <thead className="table-dark text-white">
                    <tr>
                        <th>SL</th>
                        <th>Date</th>
                        <th>revenue Type</th>
                        <th>revenue Amount</th>
                        <th>revenue Desc</th>
                        <th>Edit</th>
                    </tr>
                </thead>
                <tbody>
                    {billingData.map((bill, index) => (
                        <tr key={bill.id} style={{ lineHeight: "1", padding: "2px 0" }}>
                            <td style={{ padding: "4px" }}>
                                {(currentPage - 1) * itemsPerPage + index + 1}
                            </td>
                            <td style={{ padding: "4px" }}>{formatDate(bill.date)}</td>
                            <td style={{ padding: "4px" }}>{bill.revenue_type}</td>
                            <td style={{ padding: "4px" }}>{bill.revenue_amount}</td>
                            <td style={{ padding: "4px" }}>{bill.revenue_desc}</td>
                            <td>
                                <Button variant="warning" size="sm" onClick={() => { setEditFormData(bill); setShowEditModal(true); }}>
                                    Edit
                                </Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>}

            {/* Pagination */}
            <Pagination className="justify-content-end">
                <Pagination.Prev
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                />

                {/* First page */}
                <Pagination.Item
                    active={currentPage === 1}
                    onClick={() => setCurrentPage(1)}
                >
                    1
                </Pagination.Item>

                {/* Second page (if available) */}
                {totalPages > 1 && (
                    <Pagination.Item
                        active={currentPage === 2}
                        onClick={() => setCurrentPage(2)}
                    >
                        2
                    </Pagination.Item>
                )}

                {/* Next Button */}
                <Pagination.Next
                    onClick={() =>
                        setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                    disabled={currentPage === totalPages}
                />
            </Pagination>

            {/* Modal for adding billing data */}
            <Modal show={showModal} onHide={handleClose} backdrop="static" centered>
                <Modal.Header closeButton className="bg-primary text-white">
                    <Modal.Title>New Entry</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmit}>
                        {/* Invoice Details */}
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Bill Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="date"
                                        value={formData.date}
                                        onChange={handleChange}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Amount</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="revenue_amount"
                                        value={formData.revenue_amount}
                                        onChange={handleChange}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Revenue Type</Form.Label>
                                    <Form.Select
                                        name="revenue_type"
                                        value={formData.revenue_type}
                                        onChange={handleChange}
                                        required
                                    >
                                       <option value="scheme">Item Scheme</option>
                                            <option value="discount">Item Discount</option>
                                            <option value="credit">Quarter Credit</option>
                                            <option value="other">Other Revenue</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Group className="mb-3">
                            <Form.Label>Remarks</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                name="revenue_desc"
                                value={formData.revenue_desc}
                                onChange={handleChange}
                            />
                        </Form.Group>

                        {/* Submit Button */}
                        <div className="d-flex justify-content-end">
                            <Button variant="secondary" className="me-3" disabled={saving} onClick={handleClose}>
                                Close
                            </Button>
                            <Button variant="primary" type="submit" disabled={saving}>
                                {saving ? "Saving..." : "Save"}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>



            {/* Edit Modal */}
            <Modal show={showEditModal} backdrop="static" onHide={() => setShowEditModal(false)} centered>
                <Modal.Header closeButton className="bg-primary text-white">
                    <Modal.Title>Edit Billing Data</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {editFormData && (
                        <Form>
                            {/* Bill Number & Date */}
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Date</Form.Label>
                                        <Form.Control type="date" name="date" value={editFormData.date ? editFormData.date.split('T')[0] : ""} className="form-control-plaintext border rounded p-2 bg-light" readOnly />
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Revenue amount</Form.Label>
                                        <Form.Control type="number" name="revenue_amount" value={editFormData.revenue_amount} onChange={handleEditChange} />
                                    </Form.Group>
                                </Col>
                            </Row>


                            {/* Payment Fields */}
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>revenue_type</Form.Label>
                                        <Form.Select name="revenue_type" value={editFormData.revenue_type} onChange={handleEditChange}>
                                            <option value="scheme">Item Scheme</option>
                                            <option value="discount">Item Discount</option>
                                            <option value="credit">Quarter Credit</option>
                                            <option value="other">Other Revenue</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>



                            {/* Remarks */}
                            <Form.Group className="mb-3">
                                <Form.Label>Remarks</Form.Label>
                                <Form.Control as="textarea" rows={3} name="revenue_desc" value={editFormData.revenue_desc} onChange={handleEditChange} />
                            </Form.Group>

                            {/* Save Button */}
                            <div className="text-end">
                                <Button variant="primary" disabled={saving} onClick={handleSaveChanges}>
                                    {saving ? "Saving..." : "Save Changes"}
                                </Button>
                            </div>
                        </Form>
                    )}
                </Modal.Body>
            </Modal>


        </div>
    );
};

export default BillingData;
